import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as echarts from 'echarts';
import { getStockConfig, addStockConfig, deleteStockConfig, patchStockConfig, getQuote, getHistoricalData, getHistoricalDataList, saveHistoricalData, getInstruments } from '../services/api';
import './ConfigPage.css';

const HIST_INTERVALS = [
  { value: 'minute',   label: 'Minute'  },
  { value: '3minute',  label: '3 Min'   },
  { value: '5minute',  label: '5 Min'   },
  { value: '10minute', label: '10 Min'  },
  { value: '15minute', label: '15 Min'  },
  { value: '30minute', label: '30 Min'  },
  { value: '60minute', label: '60 Min'  },
  { value: 'day',      label: 'Day'     },
];

function getRecentWeekday(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
  return d.toISOString().substring(0, 10);
}

function HistoricalDataPopup({ symbol, onClose }) {
  const [savedEntries, setSavedEntries]     = useState([]);
  const [candles, setCandles]               = useState([]);
  const [prevDayByDate, setPrevDayByDate]   = useState({});
  const [selInterval, setSelInterval]       = useState('minute');
  const [startDate, setStartDate]           = useState(getRecentWeekday(1));
  const [startTime, setStartTime]           = useState('09:15');
  const [endDate, setEndDate]               = useState(getRecentWeekday(1));
  const [endTime, setEndTime]               = useState('15:30');
  const [pageLoading, setPageLoading]       = useState(true);
  const [pulling, setPulling]               = useState(false);
  const [error, setError]                   = useState('');
  const [showPullForm, setShowPullForm]     = useState(false);
  const chartRef      = useRef(null);
  const chartInstRef  = useRef(null);

  useEffect(() => {
    loadSavedData();
    return () => { disposeChart(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (candles.length === 0) return;
    const t = setTimeout(buildChart, 60);
    return () => { clearTimeout(t); disposeChart(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles]);

  useEffect(() => {
    const onResize = () => { if (chartInstRef.current) chartInstRef.current.resize(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const disposeChart = () => {
    if (chartInstRef.current) { chartInstRef.current.dispose(); chartInstRef.current = null; }
  };

  const loadSavedData = async () => {
    setPageLoading(true);
    try {
      const result = await getHistoricalDataList();
      const allEntries = result.data || [];
      const symEntries = allEntries.filter(e => e.symbol === symbol);
      setSavedEntries(symEntries);
      if (symEntries.length > 0) {
        const latest = symEntries[symEntries.length - 1];
        setCandles(latest.candles || []);
        setPrevDayByDate(latest.prevDayByDate || {});
        setSelInterval(latest.interval || 'minute');
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setPageLoading(false);
    }
  };

  const findInstrument = (instruments) => {
    const sym = symbol.toUpperCase();
    return instruments.find(i => i.symbol === sym && i.exchange === 'NSE')
      || instruments.find(i => i.symbol === sym)
      || null;
  };

  const handleGo = async () => {
    setError('');
    if (!startDate || !startTime || !endDate || !endTime) { setError('Fill in all date/time fields'); return; }
    const from = new Date(`${startDate}T${startTime}:00`);
    const to   = new Date(`${endDate}T${endTime}:00`);
    if (from >= to)     { setError('Start must be before end'); return; }
    if (to > new Date()) { setError('Dates cannot be in the future'); return; }

    setPulling(true);
    try {
      const instrRes  = await getInstruments();
      const instrData = findInstrument(instrRes.instruments || []);
      if (!instrData) { setError(`Symbol ${symbol} not found in instruments`); return; }

      const fromDT = `${startDate} ${startTime}:00`;
      const toDT   = `${endDate} ${endTime}:00`;
      const result = await getHistoricalData(symbol, selInterval, fromDT, toDT, instrData.token);
      const fetched = result.candles || [];
      if (fetched.length === 0) { setError('No data returned for selected range'); return; }

      const existing   = savedEntries.filter(e => e.interval === selInterval).flatMap(e => e.candles || []);
      const existingTs = new Set(existing.map(c => c[0]));
      const newCandles = fetched.filter(c => !existingTs.has(c[0]));

      if (newCandles.length === 0) {
        const merged = existing.slice().sort((a, b) => new Date(a[0]) - new Date(b[0]));
        setCandles(merged);
        setShowPullForm(false);
        return;
      }

      const allCandles = [...existing, ...newCandles].sort((a, b) => new Date(a[0]) - new Date(b[0]));

      let newPrevDay = { ...prevDayByDate };
      try {
        const firstDate  = allCandles[0][0].substring(0, 10);
        const lastDate   = allCandles[allCandles.length - 1][0].substring(0, 10);
        const prevObj    = new Date(`${firstDate}T00:00:00`);
        prevObj.setDate(prevObj.getDate() - 10);
        const prevStr    = prevObj.toISOString().substring(0, 10);
        const daily      = await getHistoricalData(symbol, 'day', `${prevStr} 09:15:00`, `${lastDate} 15:30:00`, instrData.token);
        if (daily.candles && daily.candles.length > 1) {
          const sorted = [...daily.candles].sort((a, b) => new Date(a[0]) - new Date(b[0]));
          for (let i = 1; i < sorted.length; i++) {
            const prev     = sorted[i - 1];
            const currDate = new Date(sorted[i][0]).toISOString().substring(0, 10);
            newPrevDay[currDate] = { high: prev[2], low: prev[3], open: prev[1], close: prev[4] };
          }
        }
      } catch { /* prevDay is optional */ }

      await saveHistoricalData({
        id:          `${symbol}-${startDate}-${Date.now()}`,
        symbol,
        exchange:    instrData.exchange || 'NSE',
        interval:    selInterval,
        startDate,   startTime,
        endDate,     endTime,
        candleCount: allCandles.length,
        savedAt:     new Date().toISOString(),
        candles:     allCandles,
        prevDayByDate: newPrevDay,
      });

      setPrevDayByDate(newPrevDay);
      setCandles(allCandles);
      setShowPullForm(false);
      await loadSavedData();
    } catch (err) {
      setError('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setPulling(false);
    }
  };

  const buildChart = () => {
    if (!chartRef.current || candles.length === 0) return;
    disposeChart();
    try {
      const chart = echarts.init(chartRef.current, null, { renderer: 'svg' });
      chartInstRef.current = chart;
      const candleData = [];
      const volumeData = [];
      const labels     = [];
      candles.forEach(c => {
        const dt = new Date(c[0]);
        labels.push(dt.toLocaleString('en-IN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }));
        candleData.push([parseFloat(c[1]), parseFloat(c[4]), parseFloat(c[3]), parseFloat(c[2])]);
        volumeData.push({ value: parseFloat(c[5]), itemStyle: { color: parseFloat(c[4]) >= parseFloat(c[1]) ? '#10b981' : '#ef4444' } });
      });
      chart.setOption({
        backgroundColor: 'transparent',
        textStyle: { color: '#9ca3af', fontSize: 10 },
        grid: [
          { left: '8%', right: '4%', top: 20, height: '58%' },
          { left: '8%', right: '4%', top: '72%', height: '20%' },
        ],
        xAxis: [
          { type: 'category', data: labels, gridIndex: 0, axisLabel: { fontSize: 9 }, axisLine: { lineStyle: { color: '#4b5563' } } },
          { type: 'category', data: labels, gridIndex: 1, axisLabel: { show: false } },
        ],
        yAxis: [
          { type: 'value', gridIndex: 0, scale: true, axisLabel: { fontSize: 9 }, splitLine: { lineStyle: { color: 'rgba(156,163,175,0.1)' } } },
          { type: 'value', gridIndex: 1, scale: true, axisLabel: { show: false }, splitLine: { show: false } },
        ],
        series: [
          {
            type: 'candlestick', xAxisIndex: 0, yAxisIndex: 0, data: candleData,
            itemStyle: { color: '#10b981', color0: '#ef4444', borderColor: '#10b981', borderColor0: '#ef4444' },
          },
          {
            type: 'bar', xAxisIndex: 1, yAxisIndex: 1,
            data: volumeData.map(v => ({ value: v.value, itemStyle: v.itemStyle })),
            itemStyle: { opacity: 0.5 },
          },
        ],
        tooltip: { trigger: 'axis', backgroundColor: 'rgba(0,0,0,0.8)', borderColor: '#4b5563', textStyle: { color: '#fff', fontSize: 10 } },
        dataZoom: [
          { type: 'slider', show: true, xAxisIndex: [0, 1], start: 0, end: 100, height: 18, bottom: 4 },
          { type: 'inside', xAxisIndex: [0, 1] },
        ],
      });
    } catch (err) {
      console.error('[HistoricalDataPopup] Chart error:', err);
    }
  };

  const pullForm = (
    <div className="hist-pull-form">
      <div className="hist-pull-row">
        <label>Start Date</label>
        <input type="date" value={startDate} max={getRecentWeekday(0)} onChange={e => setStartDate(e.target.value)} />
      </div>
      <div className="hist-pull-row">
        <label>Start Time</label>
        <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
      </div>
      <div className="hist-pull-row">
        <label>End Date</label>
        <input type="date" value={endDate} max={getRecentWeekday(0)} onChange={e => setEndDate(e.target.value)} />
      </div>
      <div className="hist-pull-row">
        <label>End Time</label>
        <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
      </div>
      <div className="hist-pull-row">
        <label>Interval</label>
        <select value={selInterval} onChange={e => setSelInterval(e.target.value)}>
          {HIST_INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
        </select>
      </div>
      <button className="hist-go-btn" onClick={handleGo} disabled={pulling}>
        {pulling ? 'Pulling…' : 'Go'}
      </button>
    </div>
  );

  return (
    <div className="hist-popup-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="hist-popup">
        <div className="hist-popup-header">
          <span className="hist-popup-title">📊 {symbol} — Historical Data</span>
          <button className="hist-popup-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="hist-error">{error} <button onClick={() => setError('')}>✕</button></div>}

        {pageLoading ? (
          <div className="hist-loading">Loading…</div>
        ) : candles.length === 0 ? (
          <div className="hist-pull-section">
            <p className="hist-pull-hint">No historical data saved for {symbol}. Pull data below.</p>
            {pullForm}
          </div>
        ) : (
          <div className="hist-content">
            <div ref={chartRef} className="hist-chart" />
            <div className="hist-pull-toggle-row">
              <button className="hist-pull-toggle-btn" onClick={() => setShowPullForm(f => !f)}>
                {showPullForm ? '▲ Hide' : '▼ Pull More Data'}
              </button>
              <span className="hist-candle-count">{candles.length} candles · {selInterval}</span>
            </div>
            {showPullForm && pullForm}
            <div className="hist-grid-wrap">
              <table className="hist-grid">
                <thead>
                  <tr>
                    <th>Date</th><th>Time</th><th>Open</th><th>High</th>
                    <th>Low</th><th>Close</th><th>Volume</th><th>Prev H</th><th>Prev L</th>
                  </tr>
                </thead>
                <tbody>
                  {candles.map((c, i) => {
                    const dt       = new Date(c[0]);
                    const dateStr  = dt.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });
                    const timeStr  = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
                    const cDate    = dt.toISOString().substring(0, 10);
                    const prev     = prevDayByDate[cDate] || {};
                    const isGreen  = parseFloat(c[4]) >= parseFloat(c[1]);
                    return (
                      <tr key={i} className={isGreen ? 'candle-green' : 'candle-red'}>
                        <td>{dateStr}</td>
                        <td>{timeStr}</td>
                        <td>{parseFloat(c[1]).toFixed(2)}</td>
                        <td>{parseFloat(c[2]).toFixed(2)}</td>
                        <td>{parseFloat(c[3]).toFixed(2)}</td>
                        <td>{parseFloat(c[4]).toFixed(2)}</td>
                        <td>{parseFloat(c[5]).toLocaleString()}</td>
                        <td>{prev.high ? parseFloat(prev.high).toFixed(2) : '—'}</td>
                        <td>{prev.low  ? parseFloat(prev.low).toFixed(2)  : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Pulls Kite historical data for every symbol in "Stocks in the System" in one go.
// Asks only for date range + interval — no per-stock detail, just a final
// success/failure summary once every stock has been attempted.
function BulkHistoricalDataPopup({ symbols, onClose }) {
  const [selInterval, setSelInterval] = useState('minute');
  const [startDate, setStartDate]     = useState(getRecentWeekday(1));
  const [endDate, setEndDate]         = useState(getRecentWeekday(1));
  const [pulling, setPulling]         = useState(false);
  const [progress, setProgress]       = useState({ done: 0, total: 0 });
  const [result, setResult]           = useState(null); // { success: [], failed: [{symbol, reason}] }
  const [error, setError]             = useState('');

  const findInstrument = (instruments, sym) =>
    instruments.find(i => i.symbol === sym && i.exchange === 'NSE')
    || instruments.find(i => i.symbol === sym)
    || null;

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  const handleGo = async () => {
    setError('');
    if (!startDate || !endDate) { setError('Fill in both dates'); return; }
    const from = new Date(startDate);
    const to   = new Date(endDate);
    if (from > to) { setError('Start date must be before end date'); return; }

    setPulling(true);
    setProgress({ done: 0, total: symbols.length });
    const success = [];
    const failed  = [];

    try {
      const instrRes    = await getInstruments();
      const instruments = instrRes.instruments || [];

      for (const symbol of symbols) {
        try {
          const instrData = findInstrument(instruments, symbol);
          if (!instrData) { failed.push({ symbol, reason: 'Instrument not found' }); continue; }

          const fromDT = `${startDate} 09:15:00`;
          const toDT   = `${endDate} 15:30:00`;
          const result2 = await getHistoricalData(symbol, selInterval, fromDT, toDT, instrData.token);
          const fetched = result2.candles || [];
          if (fetched.length === 0) { failed.push({ symbol, reason: 'No data returned for range' }); continue; }

          let prevDayByDate = {};
          try {
            const firstDate = fetched[0][0].substring(0, 10);
            const lastDate  = fetched[fetched.length - 1][0].substring(0, 10);
            const prevObj   = new Date(`${firstDate}T00:00:00`);
            prevObj.setDate(prevObj.getDate() - 10);
            const prevStr   = prevObj.toISOString().substring(0, 10);
            const daily     = await getHistoricalData(symbol, 'day', `${prevStr} 09:15:00`, `${lastDate} 15:30:00`, instrData.token);
            if (daily.candles && daily.candles.length > 1) {
              const sorted = [...daily.candles].sort((a, b) => new Date(a[0]) - new Date(b[0]));
              for (let i = 1; i < sorted.length; i++) {
                const prev     = sorted[i - 1];
                const currDate = new Date(sorted[i][0]).toISOString().substring(0, 10);
                prevDayByDate[currDate] = { high: prev[2], low: prev[3], open: prev[1], close: prev[4] };
              }
            }
          } catch { /* prevDay is optional */ }

          await saveHistoricalData({
            id:          `${symbol}-${startDate}-${Date.now()}`,
            symbol,
            exchange:    instrData.exchange || 'NSE',
            interval:    selInterval,
            startDate,   startTime: '09:15',
            endDate,     endTime:   '15:30',
            candleCount: fetched.length,
            savedAt:     new Date().toISOString(),
            candles:     fetched,
            prevDayByDate,
          });
          success.push(symbol);
        } catch (err) {
          failed.push({ symbol, reason: err.response?.data?.error || err.message });
        }
        setProgress(p => ({ ...p, done: p.done + 1 }));
        await sleep(300); // stay well under Kite's rate limit across the whole list
      }
      setResult({ success, failed });
    } catch (err) {
      setError('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setPulling(false);
    }
  };

  return (
    <div className="hist-popup-overlay" onClick={e => { if (e.target === e.currentTarget && !pulling) onClose(); }}>
      <div className="hist-popup">
        <div className="hist-popup-header">
          <span className="hist-popup-title">⬇ Pull Data for All Stocks ({symbols.length})</span>
          <button className="hist-popup-close" onClick={onClose} disabled={pulling}>✕</button>
        </div>

        {error && <div className="hist-error">{error} <button onClick={() => setError('')}>✕</button></div>}

        {result ? (
          <div className="hist-pull-section">
            <p className="hist-pull-hint">
              Pulled {result.success.length} of {symbols.length} stock{symbols.length !== 1 ? 's' : ''} successfully.
            </p>
            {result.failed.length > 0 && (
              <p className="hist-pull-hint">
                Failed ({result.failed.length}): {result.failed.map(f => `${f.symbol} (${f.reason})`).join(', ')}
              </p>
            )}
            <button className="hist-go-btn" onClick={onClose}>Close</button>
          </div>
        ) : (
          <div className="hist-pull-form">
            <div className="hist-pull-row">
              <label>Start Date</label>
              <input type="date" value={startDate} max={getRecentWeekday(0)} onChange={e => setStartDate(e.target.value)} disabled={pulling} />
            </div>
            <div className="hist-pull-row">
              <label>End Date</label>
              <input type="date" value={endDate} max={getRecentWeekday(0)} onChange={e => setEndDate(e.target.value)} disabled={pulling} />
            </div>
            <div className="hist-pull-row">
              <label>Interval</label>
              <select value={selInterval} onChange={e => setSelInterval(e.target.value)} disabled={pulling}>
                {HIST_INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
            </div>
            <button className="hist-go-btn" onClick={handleGo} disabled={pulling}>
              {pulling ? `Pulling… (${progress.done}/${progress.total})` : 'Go'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const MANUAL_FIELDS = [
  { key: 'target_pct',    label: 'Target %',   editable: true  },
  { key: 'stop_loss_pct', label: 'SL %',       editable: true  },
  { key: 'max_qty',       label: 'Max Qty',    editable: true  },
  { key: 'status',        label: 'Status',     editable: true  },
  { key: 'added_date',    label: 'Added',      editable: false },
];

function ConfigPage({ holdings }) {
  const [stocks, setStocks]             = useState([]);
  const [quotes, setQuotes]             = useState({});
  const [query, setQuery]               = useState('');
  const [suggestions, setSuggestions]   = useState([]);
  const [selected, setSelected]         = useState(null);
  const [adding, setAdding]             = useState(false);
  const [editCell, setEditCell]         = useState(null);
  const [editVal, setEditVal]           = useState('');
  const [error, setError]               = useState('');
  const [stocksExpanded, setStocksExpanded] = useState(false);
  const [histPopupSymbol, setHistPopupSymbol] = useState('');
  const [histPopupOpen, setHistPopupOpen]     = useState(false);
  const [bulkPullOpen, setBulkPullOpen]       = useState(false);

  const suggestRef = useRef(null);
  const autoRefreshIntervalRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);

  useEffect(() => {
    if (suggestions.length > 0 && suggestRef.current) {
      const r = suggestRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
    } else {
      setDropdownRect(null);
    }
  }, [suggestions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const holdingSymbols = new Set((holdings || []).map(h => h.tradingsymbol));

  const shouldAutoRefresh = useCallback(() => {
    const now = new Date();
    const istHours = now.getHours() + 5;
    const istMinutes = now.getMinutes() + 30;
    const totalMinutesIST = istHours * 60 + istMinutes;
    const dayOfWeek = now.getDay();
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    const isWithinTradingHours = totalMinutesIST >= 550 && totalMinutesIST <= 940;
    return isWeekday && isWithinTradingHours;
  }, []);

  const refreshQuotes = useCallback(async () => {
    if (!stocks.length) return;
    try {
      const symbols = stocks.map(s => s.symbol);
      const data = await getQuote(symbols);
      setQuotes(data || {});
    } catch (err) {
      console.error('Error refreshing quotes:', err);
    }
  }, [stocks]);

  useEffect(() => {
    getStockConfig().then(d => setStocks(d.stocks || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!stocks.length) return;
    const symbols = stocks.map(s => s.symbol);
    getQuote(symbols).then(d => setQuotes(d || {})).catch(() => {});
  }, [stocks]);

  useEffect(() => {
    if (autoRefreshIntervalRef.current) clearInterval(autoRefreshIntervalRef.current);
    if (!shouldAutoRefresh()) return;
    autoRefreshIntervalRef.current = setInterval(refreshQuotes, 1000);
    return () => {
      if (autoRefreshIntervalRef.current) clearInterval(autoRefreshIntervalRef.current);
    };
  }, [stocks, shouldAutoRefresh, refreshQuotes]);

  useEffect(() => {
    const h = (e) => { if (suggestRef.current && !suggestRef.current.contains(e.target)) setSuggestions([]); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleSearch = async (val) => {
    setQuery(val);
    setSelected(null);
    if (!val.length) { setSuggestions([]); return; }
    try {
      const response = await fetch('/instruments.json');
      const data = await response.json();
      const instruments = data.instruments || [];

      const searchVal = val.toUpperCase();
      const matches = instruments.filter(i =>
        i.symbol.includes(searchVal) || i.name.toUpperCase().includes(searchVal)
      );

      const grouped = {};
      matches.forEach(m => {
        if (!grouped[m.symbol]) grouped[m.symbol] = [];
        grouped[m.symbol].push(m);
      });

      const results = [];
      Object.values(grouped).forEach(group => {
        group.forEach(instr => results.push(instr));
      });

      setSuggestions(results.slice(0, 10));
    } catch (err) {
      console.error('Error loading instruments:', err);
      setSuggestions([]);
    }
  };

  const handleSelect = (instr) => {
    setSelected(instr);
    setQuery(`${instr.symbol} (${instr.exchange})`);
    setSuggestions([]);
  };

  const handleAdd = async () => {
    if (!selected) { setError('Please select a stock from suggestions'); return; }
    if (stocks.find(s => s.symbol === selected.symbol && s.exchange === selected.exchange)) {
      setError('Stock already in list for this exchange');
      return;
    }
    setAdding(true);
    setError('');
    try {
      const res = await addStockConfig({
        symbol: selected.symbol,
        name: selected.name,
        exchange: selected.exchange,
        token: selected.token
      });
      setStocks(res.stocks);
      setQuery('');
      setSelected(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to add stock');
    } finally { setAdding(false); }
  };

  const handleDelete = async (symbol) => {
    try {
      const res = await deleteStockConfig(symbol);
      setStocks(res.stocks);
    } catch { setError('Failed to delete'); }
  };

  const startEdit = (symbol, key, val) => { setEditCell({ symbol, key }); setEditVal(String(val ?? '')); };

  const commitEdit = async () => {
    if (!editCell) return;
    const { symbol, key } = editCell;
    try {
      const res = await patchStockConfig(symbol, { [key]: editVal });
      setStocks(prev => prev.map(s => s.symbol === symbol ? res.stock : s));
    } catch { setError('Failed to update'); }
    setEditCell(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditCell(null);
  };

  const renderEditableCell = (stock, field) => {
    const isEditing = editCell?.symbol === stock.symbol && editCell?.key === field.key;
    const val = stock[field.key];

    if (isEditing) {
      return (
        <td key={field.key} className="editing-cell">
          {field.key === 'status' ? (
            <select value={editVal} onChange={e => setEditVal(e.target.value)} onBlur={commitEdit} autoFocus>
              <option value="active">active</option>
              <option value="paused">paused</option>
            </select>
          ) : (
            <input value={editVal} onChange={e => setEditVal(e.target.value)} onBlur={commitEdit} onKeyDown={handleKeyDown} autoFocus />
          )}
        </td>
      );
    }

    return (
      <td key={field.key} className={field.editable ? 'editable-cell' : ''}
        onClick={() => field.editable && startEdit(stock.symbol, field.key, val)}
        title={field.editable ? 'Click to edit' : ''}>
        {field.key === 'status'
          ? <span className={`status-dot ${val}`}>{val}</span>
          : field.key === 'target_pct' || field.key === 'stop_loss_pct'
          ? `${val}%`
          : val}
        {field.editable && <span className="edit-hint">✎</span>}
      </td>
    );
  };

  return (
    <div className="config-page">

      {error && <div className="config-error">{error} <button onClick={() => setError('')}>✕</button></div>}

      {/* ── Stocks in the System ── */}
      <div className="config-section">
        <div className="config-section-header">
          <h3 className="config-section-title">Stocks in the System</h3>
        </div>

        <div className="config-toolbar">
          <div className="search-wrap" ref={suggestRef}>
            <input
              className="stock-search-input"
              placeholder="Search NSE symbol or company name…"
              value={query}
              onChange={e => handleSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && suggestions.length > 0 && handleSelect(suggestions[0])}
            />
            {suggestions.length > 0 && (
              <div
                className="suggestions-dropdown"
                style={dropdownRect ? {
                  position: 'fixed',
                  top:   dropdownRect.top,
                  left:  dropdownRect.left,
                  width: dropdownRect.width,
                  zIndex: 9999,
                } : undefined}
              >
                {suggestions.map((s, idx) => (
                  <div key={`${s.symbol}-${s.exchange}-${idx}`} className="suggestion-item" onClick={() => handleSelect(s)}>
                    <span className="sug-symbol">{s.symbol}</span>
                    <span className="sug-name">{s.name}</span>
                    <span className="sug-type">{s.exchange}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="add-stock-btn" data-mobile-icon="+" onClick={handleAdd} disabled={adding || !selected}>
            {adding ? 'Adding…' : '+ Add Stock'}
          </button>
          {stocks.length > 0 && (
            <button className="refresh-btn" onClick={refreshQuotes} title="Refresh quotes">
              ↻
            </button>
          )}
          {stocks.length > 0 && (
            <span className="config-legend-inline">
              <span className="legend-holding">■</span> In your Holdings
            </span>
          )}
          {stocks.length > 0 && (
            <button className="bulk-pull-btn" data-mobile-icon="⬇" onClick={() => setBulkPullOpen(true)} title="Pull historical data for all stocks">
              ⬇ Pull All Data
            </button>
          )}
          {stocks.length > 0 && (
            <button className="collapse-grid-btn" data-mobile-icon={stocksExpanded ? '▲' : '▼'} onClick={() => setStocksExpanded(e => !e)}>
              {stocksExpanded ? '▲ Hide' : `▼ Show (${stocks.length})`}
            </button>
          )}
        </div>

        {stocks.length === 0 && (
          <div className="config-empty">
            <p>No stocks configured yet.</p>
            <p>Search and add NSE stocks above to get started.</p>
          </div>
        )}

        {stocks.length > 0 && stocksExpanded && (
          <div className="config-table-wrap">
            <table className="config-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Exchange</th>
                  <th>Token</th>
                  <th>Price</th>
                  <th>Day High</th>
                  <th>Day Low</th>
                  <th>Volume</th>
                  <th>Bid</th>
                  <th>Ask</th>
                  {MANUAL_FIELDS.map(f => <th key={f.key}>{f.label}</th>)}
                  <th>Chart</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {stocks.map(stock => {
                  const q = quotes[stock.symbol] || {};
                  const inHolding = holdingSymbols.has(stock.symbol);
                  return (
                    <tr key={`${stock.symbol}-${stock.exchange}`} className={inHolding ? 'in-holding' : ''}>
                      <td><span className="symbol-chip">{stock.symbol}</span></td>
                      <td>{stock.exchange || '—'}</td>
                      <td>{stock.token || '—'}</td>
                      <td>{q.lastPrice != null ? `₹${q.lastPrice.toFixed(2)}` : '—'}</td>
                      <td>{q.dayHigh != null ? `₹${q.dayHigh.toFixed(2)}` : '—'}</td>
                      <td>{q.dayLow != null ? `₹${q.dayLow.toFixed(2)}` : '—'}</td>
                      <td>{q.volume != null ? (q.volume / 1000).toFixed(0) + 'K' : '—'}</td>
                      <td>{q.bid != null ? `₹${q.bid.toFixed(2)}` : '—'}</td>
                      <td>{q.ask != null ? `₹${q.ask.toFixed(2)}` : '—'}</td>
                      {MANUAL_FIELDS.map(field => renderEditableCell(stock, field))}
                      <td>
                        <button className="hist-chart-btn" onClick={() => { setHistPopupSymbol(stock.symbol); setHistPopupOpen(true); }} title="Historical Data">📊</button>
                      </td>
                      <td>
                        <button className="delete-btn" onClick={() => handleDelete(stock.symbol)} title="Remove">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {histPopupOpen && (
        <HistoricalDataPopup
          symbol={histPopupSymbol}
          onClose={() => setHistPopupOpen(false)}
        />
      )}

      {bulkPullOpen && (
        <BulkHistoricalDataPopup
          symbols={stocks.map(s => s.symbol)}
          onClose={() => setBulkPullOpen(false)}
        />
      )}
    </div>
  );
}

export default ConfigPage;
